import { t, Trans } from '@lingui/macro'

import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  Grid,
  Typography
} from '@mui/material'
import { dateFormat } from 'app/appSettings'
import { updateErrorLogByFlow } from 'app/backend/selesforce-custom-queries/ErrorLogQueries'
import { useGlobalQueries } from 'app/GlobalQueriesProvider'
import { ReduxOrganization, ReduxState, ReduxUser } from 'app/redux/ReduxInterfaces'
import { matchPath, RouteConfig } from 'app/router/Routes'
import sfOauthConfig from 'app/services/sfAuth/sfAuthConfig'
import { createCaseByFlow } from 'app/services/sfAuth/sfData/sfCase'
import { CASE_TYPES } from 'app/services/sfAuth/sfData/sfCaseTypes'
import { linkFileByFlow, uploadFile } from 'app/services/sfAuth/sfData/sfFiles'
import { useFormikContext } from 'formik'
import cloneDeep from 'lodash/cloneDeep'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { myI18n } from 'translation/I18nConnectedProvider'
import { DialogTitleWithIconClose } from '../common-components/DialogTitleWithIconClose'
import { FormTopLabel } from '../common-components/forms/FormTopLabel'
import { TextFieldWithCustomControl } from '../common-components/with-formik/TextFieldWithCustomControl'
import { useFormContext } from '../forms/form-page/FormContext'
import { ERROR_REPORTING_USER_ID } from '../forms/FormErrorPopUp'
import { requiredTrans } from '../forms/formTranslations'
import { downloadOrphanedPDF } from '../forms/pdf-components/formPdfUtils'
import fundingRoutes from '../funding/FundingRoutes'
import grantsRoutes from '../grants/GrantsRoutes'
import dmahRoutes from '../internal/DMAH/dmahRoutes'
import internalRoutes from '../internal/InternalRoutes'
import { resourceInventoryRoutes } from '../resources/ResourceRoutes'
import satRoutes from '../self-assessment/SATRoutes'
import sessionRoutes from '../sessions/SessionRoutes'
import surveyRoutes from '../surveys/SurveyRoutes'
import vivreEnVilleRoutes from '../vivre-en-ville/VivreEnVilleRoutes'
import formsRoutes from './formsRoutes'
import { getObjectsIdsFromConnectedMap } from './formUtils'
import { ConnectedMap } from './types/FormTypes'

interface FormValuesData {
  elementType: 'string'
  id: string
  sectionIndex: number
  sectionName: string
  showBasicElements: boolean
  title: string
  value: any
}

interface SavingFailedWarningDialogProps {
  fileName: string
  relatedErrorLogId?: string
  open: boolean
  handleClose: () => void
  data?: {
    error: string
    savingResult: any
    connectedMap: ConnectedMap
    formData: Record<string, FormValuesData> & { type: string }
    current: Record<string, FormValuesData>
    disabledIds?: string[]
  }
  generatePDF: (fileName?: string) => React.ReactElement
  showPDFButton: boolean
}

const KNOWN_ERRORS: Record<string, { code: string }> = {
  INSUFFICIENT_ACCESS_OR_READONLY: {
    code: '01'
  }
}

const SavingFailedWarningDialog = ({ handleClose, open, data, fileName, generatePDF, relatedErrorLogId, showPDFButton }: SavingFailedWarningDialogProps): JSX.Element => {
  const [reportErrorOpen, setReportErrorOpen] = useState(false)
  const [errorDescription, setErrorDescription] = useState('')
  const [submittingCase, setSubmittingCase] = useState<boolean>(false)
  const [thankYouOpen, setThankYouOpen] = useState<boolean>(false)
  const [exporting, setExporting] = useState(false)
  const user = useSelector<ReduxState, ReduxUser>(state => state.user)
  const organization = useSelector<ReduxState, ReduxOrganization>((state) => state.organization)
  const { network } = useGlobalQueries()
  const { values } = useFormikContext()
  const history = useHistory()

  const { mainConnectedObject } = useFormContext()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const errorCodeLabel = data != null ? KNOWN_ERRORS[data?.error]?.code : undefined

  const routes: RouteConfig[] = [
    ...resourceInventoryRoutes,
    ...fundingRoutes,
    ...internalRoutes,
    ...grantsRoutes,
    ...sessionRoutes,
    ...formsRoutes,
    ...vivreEnVilleRoutes,
    ...dmahRoutes,
    ...satRoutes,
    ...surveyRoutes
  ]

  const handleDone = (): void => {
    const pathname = history.location.pathname
    const routeObj: RouteConfig | undefined = routes.find(obj => matchPath(obj.path, pathname))
    if (routeObj?.errorRedirectPath != null) {
      history.replace(routeObj.errorRedirectPath)
    } else {
      history.push('/')
    }
  }

  const handleDownloadPdf = async (): Promise<void> => {
    setExporting(true)

    const pdfDownloadKey = enqueueSnackbar(myI18n._(t`Generating PDF`), {
      variant: 'info',
      persist: true
    })
    try {
      const pdfDocument = generatePDF()
      await downloadOrphanedPDF(pdfDocument, fileName).then(result => {
        closeSnackbar(pdfDownloadKey)
        setExporting(false)
        enqueueSnackbar(myI18n._(t`PDF generated successfully`), {
          variant: 'success'
        })
      })
    } catch (err) {
      setExporting(false)
      closeSnackbar(pdfDownloadKey)
      enqueueSnackbar(myI18n._(t`Error generating PDF`), {
        variant: 'error'
      })
    }
  }

  const submitErrorReport = async (): Promise<void> => {
    if (data == null) {
      return
    }
    setSubmittingCase(true)
    const toPass: any = cloneDeep(values)
    delete toPass.muInfo
    delete toPass.muUsers

    const json = JSON.stringify({
      reject: data?.savingResult,
      error: data?.error,
      errorCodeLabel,
      values: toPass
    })

    const { connectedAccount, connectedOpportunity, connectedResource } = getObjectsIdsFromConnectedMap(data.connectedMap, mainConnectedObject)

    const caseName = `Saving failed ${moment.utc().format(dateFormat)}`
    createCaseByFlow({
      title: caseName,
      type: CASE_TYPES.SAVING_FAILED,
      language: user.language,
      location: sfOauthConfig.oauth2.redirectUri + history.location.pathname,
      resource: connectedResource,
      contact: user.userObject.contactId,
      opportunityId: connectedOpportunity,
      description: errorDescription,
      organization: connectedAccount ?? organization.id,
      owner: ERROR_REPORTING_USER_ID
    }).then(result => {
      const caseId = result[0].outputValues.createdId
      uploadFile(
        { name: caseName + '.json', uploadId: undefined },
        json,
        network.id,
        true
      )
        .then(result => {
          const fileId = result.Id
          Promise.all([
            linkFileByFlow({ file: fileId, target: caseId }),
            relatedErrorLogId != null && updateErrorLogByFlow({
              id: relatedErrorLogId,
              caseId
            })
          ]).finally(result => {
            setSubmittingCase(false)
            setThankYouOpen(true)
          })
        })
    }).catch(error => {
      console.error(error)
      setSubmittingCase(false)
      handleDone()
    })
  }

  return (
    <>
      <Dialog maxWidth='xs' fullWidth open={thankYouOpen}>
        <DialogTitleWithIconClose
          handleClose={() => {
            setThankYouOpen(false)
            handleDone()
          }}
          label={<Trans>THANK_YOU</Trans>}
        />
        <DialogContent>
          <DialogContentText>
            <Trans>CASE_SUBMITTED_THANK_YOU_TEXT</Trans>
          </DialogContentText>
          <Button
            variant='outlined'
            fullWidth
            sx={{ mt: '8px' }}
            onClick={e => {
              setThankYouOpen(false)
              handleDone()
            }}
          >
            <Trans>CLOSE</Trans>
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog maxWidth='xs' fullWidth open={reportErrorOpen && !thankYouOpen}>
        <DialogTitleWithIconClose
          handleClose={() => {
            setReportErrorOpen(false)
          }}
          disabled={submittingCase}
          label={<Trans>REPORT_ERROR</Trans>}
        />
        <DialogContent>
          <Typography className='required-fields-label' sx={{ pt: '0px' }}>
            <Trans>
              REQUIRED_FIELDS_PROMPRT_EDITABLE_FORM
            </Trans>
          </Typography>

          <FormTopLabel required>
            <Trans>
              REPORT_ERROR_DESCRIPTION_LABEL
            </Trans>
          </FormTopLabel>

          <TextFieldWithCustomControl
            value={errorDescription}
            handleChange={v => {
              setErrorDescription(v)
            }}
            multiline
            rows={7}
            fullWidth
            required
            disabled={submittingCase}
            error={errorDescription === ''}
            errorLabel={requiredTrans}
          />
          <Grid sx={{ mt: '32px' }} container direction='column' gap='16px'>
            <Button
              color='primary'
              variant='contained'
              fullWidth
              disabled={submittingCase || errorDescription === ''}
              onClick={() => {
                submitErrorReport().catch(err => {
                  console.error(err)
                })
              }}
            >
              <Trans>SUBMIT</Trans>
            </Button>

            <Button
              color='primary'
              variant='outlined'
              fullWidth
              disabled={submittingCase}
              onClick={() => {
                setReportErrorOpen(false)
              }}
            >
              <Trans>CANCEL</Trans>
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog maxWidth='xs' fullWidth open={open && !reportErrorOpen && !thankYouOpen}>
        <DialogTitleWithIconClose
          handleClose={handleDone}
          label={<Trans>ERROR</Trans>}
        />
        <DialogContent>
          <Alert
            severity='error'
          >
            <AlertTitle>
              <Trans>
                FORM_COULD_NOT_BE_SAVED
              </Trans>
              {errorCodeLabel != null && ` (${myI18n._(t`ERROR_CODE`)} ${errorCodeLabel})`}
            </AlertTitle>
            <Typography>
              <Trans>
                FORM_SAVING_FAILED_DOWNLOAD_ANSWER_PROMPT
              </Trans>
            </Typography>
            <Typography>
              <b>
                <Trans>FORM_SAVING_FAILED_DO_NOT_CONTINUE_EDITING_PROMPT</Trans>
              </b>{' '}
              <Trans>FORM_SAVING_FAILED_DO_NOT_CONTINUE_EDITING_PROMPT_CONT</Trans>
            </Typography>
          </Alert>

          <Grid sx={{ mt: '32px' }} container direction='column' gap='16px'>
            {showPDFButton && (
              <Button
                color='primary'
                variant='contained'
                fullWidth
                disabled={exporting}
                onClick={() => {
                  void handleDownloadPdf()
                }}
              >
                <Trans>FORM_DOWNLOAD_PDF</Trans>
              </Button>
            )}

            <Button
              color='primary'
              variant='outlined'
              fullWidth
              onClick={() => {
                setReportErrorOpen(true)
              }}
            >
              <Trans>REPORT_ERROR</Trans>
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default SavingFailedWarningDialog
